import { CenteredScrollPage } from '@/components/shared/CenteredScrollPage';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useSendPasswordResetEmail } from '@/hooks/useAuthMutations';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle, Loader2, Lock } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Notice } from '@/components/ui/notice';

// Form validation schema
const emailSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type EmailFormData = z.infer<typeof emailSchema>;

export default function PasswordResetRequest() {
  const navigate = useNavigate();
  const sendEmailMutation = useSendPasswordResetEmail();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    mode: 'onBlur',
  });

  const isPending = sendEmailMutation.isPending;

  if (sendEmailMutation.isSuccess) {
    return (
      <CenteredScrollPage>
        <Card className="w-full max-w-sm">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <CheckCircle className="h-16 w-16 text-success" />
            </div>
            <CardTitle className="text-center">Check Your Email</CardTitle>
            <CardDescription className="text-center">
              Password reset instructions have been sent to your email
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Notice tone="success" className="p-4">
                <p className="text-sm text-center">
                  Check your email for a password reset link. Click the link to set your new password.
                </p>
              </Notice>

              <Button 
                className="w-full"
                onClick={() => navigate('/login')}
              >
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </CenteredScrollPage>
    );
  }

  return (
    <CenteredScrollPage>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <div className="flex items-center justify-center mb-4">
            <Lock className="h-12 w-12" />
          </div>
          <CardTitle className="text-center">Reset Password</CardTitle>
          <CardDescription className="text-center">
            Enter your email address to receive password reset instructions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit((data) => sendEmailMutation.mutate(data))} className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder="Email address"
                {...register('email')}
                disabled={isPending}
              />
              {errors.email && (
                <p className="text-destructive text-sm mt-1">
                  {errors.email.message}
                </p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={isPending || isSubmitting}
            >
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Instructions'
              )}
            </Button>
          </form>

          <Button
            variant="ghost"
            onClick={() => navigate('/login')}
            disabled={isPending}
            className="w-full mt-4"
          >
            Back to Login
          </Button>

          {sendEmailMutation.isError && (
            <p className="text-destructive mt-4 text-sm">
              {sendEmailMutation.error?.message || 'Failed to send reset email. Please try again.'}
            </p>
          )}
        </CardContent>
      </Card>
    </CenteredScrollPage>
  );
}

